import React, { useMemo, useState } from 'react';
import axios from 'axios';

function AuthPage({ onAuthenticated }) {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const title = useMemo(() => (mode === 'signup' ? 'Create Your Account' : 'Welcome Back'), [mode]);
  const subtitle = useMemo(
    () => (mode === 'signup'
      ? 'Sign up with your name, email, and password.'
      : 'Log in with your email and password.'),
    [mode]
  );

  const updateFormField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setForm({
      name: '',
      email: '',
      password: ''
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.email.trim() || !form.password) {
      setError('Email and password are required.');
      return;
    }

    if (mode === 'signup' && !form.name.trim()) {
      setError('Name is required.');
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === 'signup' ? '/api/auth/signup' : '/api/auth/login';
      const payload = mode === 'signup'
        ? { name: form.name.trim(), email: form.email.trim(), password: form.password }
        : { email: form.email.trim(), password: form.password };

      const response = await axios.post(endpoint, payload);
      onAuthenticated(response.data);
    } catch (submitError) {
      setError(submitError.response?.data?.error || submitError.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="/spiderman-logo-white.png" alt="Automation" className="auth-logo" />
          <div>
            <p>SPIDER WEB AUTOMATION</p>
            <strong>Control Panel</strong>
          </div>
        </div>

        <h1>{title}</h1>
        <p className="auth-subtitle">{subtitle}</p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateFormField('name', e.target.value)}
                placeholder="Enter your full name"
                autoComplete="name"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateFormField('email', e.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateFormField('password', e.target.value)}
              placeholder="Enter your password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
            />
          </div>

          <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
            {loading ? 'Please wait...' : (mode === 'signup' ? 'Sign Up' : 'Log In')}
          </button>
        </form>

        <div className="auth-switch">
          {mode === 'signup' ? (
            <p>
              Already have an account?{' '}
              <button type="button" onClick={() => resetForMode('login')}>
                Log In
              </button>
            </p>
          ) : (
            <p>
              New user?{' '}
              <button type="button" onClick={() => resetForMode('signup')}>
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
